import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiCopy } from 'react-icons/fi';
import { BsCheckCircleFill } from 'react-icons/bs';

import mtnLogo from "../../assets/images/mtn.png";
import gloLogo from "../../assets/images/glo.png";
import airtelLogo from "../../assets/images/airtel1.png";
import gmobileLogo from "../../assets/images/9mobile.png";
import deposiLogo from "../../assets/images/wallet.png";

const networkLogos = {
  MTN: mtnLogo,
  GLO: gloLogo,
  AIRTEL: airtelLogo,
  '9MOBILE': gmobileLogo,
  Deposit: deposiLogo,
  FUNDING: deposiLogo,
  PAYSTACK_FUNDING: deposiLogo,
};

export default function TransactionDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get transaction data passed from History page
  const transaction = location.state?.transaction;

  // Fallback if no data is passed
  if (!transaction) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Transaction not found</p>
          <button 
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-[#3866A3] text-white rounded-full"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const isSuccess = transaction.status?.toLowerCase() === "successful" || 
                    transaction.status?.toLowerCase() === "success";

  const copyToClipboard = (text, message = "Copied!") => {
    navigator.clipboard.writeText(text);
    alert(message);
  };

  // Safe destructuring of metadata parameters
  const metadata = transaction.metadata || {};
  const category = transaction.category || "";

  // Dynamic asset mapping for the top header
  const getSubtext = () => {
    if (category === "ELECTRICITY") return metadata.disco || "Electricity";
    if (category === "AIRTIME") return metadata.network || "Airtime";
    if (category === "FUNDING" || category === "PAYSTACK_FUNDING") return metadata.provider || "Deposit";
    return transaction.title || "Transaction";
  };

  const getLogoSource = () => {
    const key = (metadata.network || category || "").toUpperCase();
    return networkLogos[key] || networkLogos['Deposit'];
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-20 border-b px-4 py-4 flex items-center justify-between">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-xl text-gray-700"
        >
          ← <span className="font-semibold">Transaction Details</span>
        </button>
        <span className="text-[#3866A3] font-medium cursor-pointer">Support</span>
      </div>

      <div className="px-4 pt-6">
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          {/* Network / Provider & Amount */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <img 
                src={getLogoSource()} 
                alt="Transaction type icon"
                className="w-10 h-10 object-contain"
              />
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-1">
              ₦{Math.abs(transaction.amountRaw || transaction.amount || 0).toLocaleString()}
            </h1>
            <p className="text-sm font-semibold tracking-wide uppercase text-gray-400 mt-1">
              {getSubtext()}
            </p>
          </div>

          {/* Status Badge */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {isSuccess ? (
              <BsCheckCircleFill className="text-green-500 text-2xl" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-red-500 text-sm font-bold">✕</span>
              </div>
            )}
            <span className={`font-semibold text-base ${isSuccess ? "text-green-600" : "text-red-600"}`}>
              {transaction.status || "Completed"}
            </span>
          </div>

          {/* Electricity Token Section */}
          {category === "ELECTRICITY" && metadata.token && (
            <div className="mt-6 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/70 text-center">
              <span className="text-xs text-blue-600 font-medium uppercase tracking-wider block mb-1">
                Electricity Token
              </span>
              <div className="flex items-center justify-center gap-2">
                <span className="text-xl font-mono font-bold text-gray-900 tracking-wide break-all">
                  {metadata.token}
                </span>
                <button 
                  onClick={() => copyToClipboard(metadata.token, "Token copied to clipboard!")}
                  className="text-[#3866A3] p-1 hover:bg-blue-100 rounded transition-colors"
                >
                  <FiCopy size={16} />
                </button>
              </div>
              {metadata.units && (
                <span className="text-xs text-gray-500 block mt-1">
                  Units: {metadata.units}
                </span>
              )}
            </div>
          )}

          <div className="border-t border-dashed border-gray-300 my-6" />

          {/* Core Transaction Parameters */}
          <div className="space-y-5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Transaction Type</span>
              <span className="font-medium">{transaction.title || transaction.type}</span>
            </div>

            {/* CONDITIONAL META RENDERING BASED ON SOURCE CATEGORY */}
            
            {/* Electricity Details */}
            {category === "ELECTRICITY" && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Meter Number</span>
                  <span className="font-mono font-medium">{metadata.meterNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Meter Type</span>
                  <span className="font-medium capitalize">{metadata.meterType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recipient Phone</span>
                  <span className="font-medium">{metadata.phone}</span>
                </div>
              </>
            )}

            {/* Airtime Details */}
            {category === "AIRTIME" && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone Number</span>
                  <span className="font-medium">{metadata.phone || transaction.recipient}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Network Provider</span>
                  <span className="font-medium uppercase">{metadata.network}</span>
                </div>
              </>
            )}

            {/* Deposit Funding Details */}
            {(category === "FUNDING" || category === "PAYSTACK_FUNDING") && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Channel</span>
                  <span className="font-medium text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-700">
                    {metadata.channel?.replace('_', ' ') || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Number</span>
                  <span className="font-mono font-medium">{metadata.accountNumber || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Gross Amount</span>
                  <span className="font-medium">₦{Number(metadata.grossAmount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Gateway Fee ({metadata.feePercent || 0}%)</span>
                  <span className="font-medium text-red-500">-₦{Number(metadata.feeAmount || 0).toFixed(2)}</span>
                </div>
              </>
            )}

            {/* Cashback Details */}
            {category === "CASHBACK" && (
              <div className="flex justify-between">
                <span className="text-gray-600">Originating Txn ID</span>
                <span className="font-mono font-medium truncate max-w-[180px]">
                  {metadata.baseTransaction}
                </span>
              </div>
            )}

            {/* Refund Details */}
            {category === "REFUND" && (
              <div className="flex justify-between">
                <span className="text-gray-600">Failed Txn Reference</span>
                <span className="font-mono font-medium truncate max-w-[180px]">
                  {metadata.baseTransaction}
                </span>
              </div>
            )}

            <div className="border-t border-gray-100 my-2" />

            {/* Financial Reconciliation Row */}
            <div className="flex justify-between">
              <span className="text-gray-600">Net Wallet Impact</span>
              <span className={`font-bold ${transaction.type === "CREDIT" ? "text-green-600" : "text-gray-900"}`}>
                {transaction.type === "CREDIT" ? "+" : "-"}₦{Math.abs(transaction.amountRaw || transaction.amount || 0).toLocaleString()}.00
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-gray-600">Reference ID</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-500 break-all max-w-[180px]">
                  {transaction.reference || transaction.id}
                </span>
                <button 
                  onClick={() => copyToClipboard(transaction.reference || transaction.id, "Reference ID copied!")}
                  className="text-[#3866A3] active:scale-90"
                >
                  <FiCopy size={16} />
                </button>
              </div>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Date &amp; Time</span>
              <span className="font-medium text-right text-gray-700">
                {new Date(transaction.createdAt).toLocaleDateString('en-GB')} at {new Date(transaction.createdAt).toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit' 
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mt-8 px-2">
          <button 
            onClick={() => alert("Support ticket pipeline fallback triggered")}
            className="flex-1 py-4 border border-gray-300 rounded-full text-gray-700 font-medium active:bg-gray-100 transition-colors"
          >
            Report Issue
          </button>

          <button 
            onClick={() => alert("Receipt generation pipeline active")}
            className="flex-1 py-4 bg-[#3866A3] text-white rounded-full font-medium active:bg-[#2d5a8c] transition-colors"
          >
            Share Receipt
          </button>
        </div>
      </div>
    </div>
  );
}